import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'
import { inputFieldStates } from 'ui/components/form-components-v2/inputStates.styles'

export const TicketModalRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${theme.colors.surfaceContainerBackground};
  border-radius: ${theme.attributes.borderRadius.quaternary};
  font-family: ${theme.typography.fontFamily.primary};
`

export const TicketModalHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 96px;
  box-sizing: border-box;
  padding: 0 ${theme.spacing.large}px;
  border-bottom: 1px solid ${theme.colors.strokeSecondary};

  .title-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xxxLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: normal;
  }

  .cpi-chip {
    display: flex;
    align-items: center;
    height: 24px;
    padding: 2px ${theme.spacing.xSmall}px;
    box-sizing: border-box;
    border-radius: 18px;
    background: ${theme.colors.cardFillQuaternary};
    color: ${theme.colors.devicesSecondaryText};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
  }

  .close-button {
    width: 40px;
    height: 40px;

    html[data-theme='light'] & svg path {
      fill: ${theme.colors.menuItemText};
    }
  }
`

export const TicketModalBody = styled(Box)`
  display: flex;
  flex: 1;
  min-height: 0;
`

export const StepsList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xSmall}px;
  width: 249px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: ${theme.spacing.medium}px ${theme.spacing.large}px;
  border-right: 1px solid ${theme.colors.strokeSecondary};

  .step {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 48px;
  }

  .step-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    box-sizing: border-box;
    border-radius: ${theme.attributes.borderRadius.circle};
    border: 1px solid ${theme.colors.tooltipBg};
    color: ${theme.colors.textCaption};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;
  }

  .step-label {
    color: ${theme.colors.textCaption};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 22px;
  }

  .step.active {
    .step-icon {
      border-color: ${theme.colors.cta};
      background: ${theme.colors.cta};
      color: ${theme.colors.pageBackground};
    }

    .step-label {
      color: ${theme.colors.devicesSecondaryText};
    }
  }

  .step.done .step-icon {
    border-color: ${theme.colors.cardFillQuaternary};
    background: ${theme.colors.tooltipBg};
    color: ${theme.colors.textCaption};
  }
`

export const StepPanel = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  .step-content {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.small}px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: ${theme.spacing.medium}px ${theme.spacing.large}px;
  }

  .section-title {
    color: ${theme.colors.menuItemText};
    font-size: ${theme.typography.fontSize.header};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 36px;
    letter-spacing: 0.1px;
  }

  .section-description {
    color: ${theme.colors.devicesSecondaryText};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.xSmall}px;
  }

  .field-label {
    color: ${theme.colors.devicesSecondaryText};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;

    .required {
      color: #ee4359;
      margin-left: 2px;
    }
  }

  .field-hint {
    color: ${theme.colors.textCaption};
    font-size: ${theme.typography.fontSize.medium};
    line-height: 16px;
  }

  .empty-message,
  .loading-message {
    color: ${theme.colors.devicesSecondaryText};
    font-size: ${theme.typography.fontSize.large};
    line-height: 20px;
  }

  /* The UI Library's field states: default, hover, focused (value and chevron turn CTA) and disabled. */
  .MuiInputBase-root {
    ${inputFieldStates()}
    min-height: 48px;
    background: transparent;
    color: ${theme.colors.mainSecondary};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    letter-spacing: 0.1px;

    svg {
      color: ${theme.colors.devicesSecondaryText};
    }

    &.is-empty .MuiSelect-select {
      color: ${theme.colors.mainSecondary};
    }

    &.Mui-focused {
      .MuiSelect-select,
      .MuiSelect-icon {
        color: ${theme.colors.cta};
      }
    }

    &.Mui-disabled {
      border-color: ${theme.colors.strokeSecondary};
      background: transparent;

      .MuiSelect-select,
      .MuiInputBase-input,
      .MuiInputBase-input::placeholder,
      svg {
        color: ${theme.colors.btnSecondaryDisabledText};
        -webkit-text-fill-color: ${theme.colors.btnSecondaryDisabledText};
      }
    }
  }

  .MuiInputBase-input::placeholder {
    color: ${theme.colors.mainSecondary};
    opacity: 1;
  }

  /* Values typed into free-text list fields (Labels, Customers); MUI's default chip text is near-black. */
  .MuiAutocomplete-tag.MuiChip-root {
    height: 28px;
    border: 1px solid ${theme.colors.dropdownBorder};
    border-radius: ${theme.attributes.borderRadius.secondary};
    background: ${theme.colors.cardFillSecondary};
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.bold};

    .MuiChip-deleteIcon {
      color: ${theme.colors.devicesSecondaryText};
      font-size: 16px;

      &:hover {
        color: ${theme.colors.cta};
      }
    }
  }

  .date-input {
    width: 100%;
  }
`

export const StepActions = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing.xSmall}px;
  min-height: 104px;
  box-sizing: border-box;
  padding: ${theme.spacing.medium}px ${theme.spacing.large}px;
  border-top: 1px solid ${theme.colors.strokeSecondary};

  .server-error {
    flex: 1;
    color: ${theme.colors.errorLight};
    font-size: ${theme.typography.fontSize.medium};
    line-height: 16px;
  }

  button {
    width: 140px;
    height: 48px;
  }
`

export const RecordsTable = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .records-row {
    display: grid;
    grid-template-columns: var(--records-columns);
    align-items: center;
    gap: ${theme.spacing.small}px;
    min-height: 60px;
    box-sizing: border-box;
    padding: 0 ${theme.spacing.xSmall}px 0 ${theme.spacing.medium}px;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    border: 1px solid ${theme.colors.strokeSecondary};
    color: ${theme.colors.menuItemText};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;

    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    > span:first-of-type {
      font-weight: 700;
    }
  }

  .records-row__remove {
    justify-self: center;
    width: 28px;
    height: 28px;
    color: ${theme.colors.devicesSecondaryText};
    opacity: 0;
    transition: opacity 120ms ease;

    &:hover {
      color: ${theme.colors.cta};
    }
  }

  .records-row:hover .records-row__remove,
  .records-row__remove:focus-visible {
    opacity: 1;
  }

  .records-header {
    min-height: 50px;
    background: ${theme.colors.devicesHeaderBg};
    color: ${theme.colors.devicesSecondaryText};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;

    > span {
      display: flex;
      align-items: center;
      gap: ${theme.spacing.xSmall}px;
    }

    > span:first-of-type {
      font-weight: ${theme.typography.fontWeight.normal};
    }

    .records-header__label {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* The grid's column filter icon, shown for parity with its headers; the summary has no filters. */
    .records-header__icon {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
      background-color: currentColor;
      mask: url('/devices-filter-icon.svg') center / contain no-repeat;
    }
  }
`
